import { useQuery } from '@tanstack/react-query';
import first from 'lodash/first';
import { Locale } from 'chaika-enums';

import type { Optional } from 'chaika-common-types';
import type { PracticeContactViewModel } from 'chaika-types';
import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { OptionType } from '@components/Select';
import { store } from '@store';
import { getPracticeName } from '@organization/utils/localizedNames';
import { practiceQueryOptions } from '@modules/clinics/queries/practiceQueries';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';
import { isDefined } from '@utils/isDefined';

import { isPracticeOfOrganization } from '../utils/isPracticeOfOrganization';

interface PracticeLabelParams {
    practice: PracticeContactViewModel;
    locale: Optional<Locale>;
    organizationId: Optional<string>;
}

interface Data {
    practiceOptions: OptionType<string>[];
    selectedPracticeId: Nullable<string>;
    practiceNames: string[];
    onPracticeSelect(option: Nullable<OptionType>): void;
}

export function useTaskPractice(): Data {
    const { state, actions } = useTaskForm();
    const { data: practices = [] } = useQuery(practiceQueryOptions.contacts());
    const { selectedLanguage: locale, organizationId } = store.authUser;

    const practiceOptions = practices
        .filter(
            practice => isPracticeOfOrganization(practice, organizationId) || state.practiceIds.includes(practice.id),
        )
        .map(practice => ({ value: practice.id, label: getPracticeLabel({ practice, locale, organizationId }) }))
        .sort((left, right) => left.label.localeCompare(right.label));

    const onPracticeSelect = (option: Nullable<OptionType>): void => {
        actions.setPractices(option ? [String(option.value)] : []);
    };

    return {
        practiceOptions,
        selectedPracticeId: first(state.practiceIds) ?? null,
        practiceNames: practiceOptions
            .filter(({ value }) => state.practiceIds.includes(value))
            .map(({ label }) => label),
        onPracticeSelect,
    };
}

function getPracticeLabel({ practice, locale, organizationId }: PracticeLabelParams): string {
    const name = getPracticeName(practice, locale);

    if (isDefined(organizationId)) {
        return name;
    }

    return practice.clinicText ? `${name} (${practice.clinicText})` : name;
}
